import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { formatCarats, formatGrams } from '@/lib/format';

/**
 * Attribute Dialog — moves part (or all) of an issue line's unattributed
 * material on to a specific order (+ optional requirement).
 *
 * Props:
 *  - open, onOpenChange
 *  - companyId
 *  - goldsmithId (preferred): we then auto-load active orders for that goldsmith.
 *  - issueId, lineId: identifies the unattributed bucket source.
 *  - lineMeta: {
 *      material_type: 'diamond' | 'gold',
 *      packet_no, classification_schema,         // for diamond
 *      purity_name, purity_id,                   // for gold
 *      available: { carats, pieces } OR { grams } — strict caps
 *    }
 *  - issueMemoNo (optional, for header context)
 *  - pool (optional): when provided, the dialog operates in POOLED mode —
 *      it attributes from the goldsmith's consolidated unattributed pool for
 *      one classification (purity/packet) rather than a single memo line.
 *      Shape: {
 *        material_type, purity_id?, packet_id?,
 *        purity_name?, packet_no?, classification_schema?,
 *        available: { grams } | { carats, pieces }   // pooled totals (caps)
 *      }
 *  - onSuccess(): callback after successful attribution
 */
export default function AttributeDialog({
  open, onOpenChange,
  companyId, goldsmithId,
  issueId, lineId, lineMeta,
  issueMemoNo,
  pool,
  onSuccess,
}) {
  // Wave — pooled attribution consolidates every unattributed source line of
  // one classification into a single action. ``meta`` unifies the source
  // descriptor for both single-line and pooled modes.
  const poolMode = !!pool;
  const meta = pool || lineMeta;
  const isGold = meta?.material_type === 'gold';
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [requirementId, setRequirementId] = useState('');
  const [markComplete, setMarkComplete] = useState(false);
  const [carats, setCarats] = useState('');
  const [pieces, setPieces] = useState('');
  const [grams, setGrams] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset on close
  useEffect(() => {
    if (!open) {
      Promise.resolve().then(() => {
        setOrderId(''); setRequirementId(''); setRequirements([]);
        setMarkComplete(false); setCarats(''); setPieces(''); setGrams('');
        setNote('');
      });
    }
  }, [open]);

  // Load active orders for goldsmith
  useEffect(() => {
    if (!open || !goldsmithId) return;
    let cancelled = false;
    api
      .get(`/orders/by-goldsmith/${goldsmithId}`, { params: { status: 'open' } })
      .then(({ data }) => { if (!cancelled) setOrders(data || []); })
      .catch((e) => toast.error(extractError(e, 'Failed to load orders')));
    return () => { cancelled = true; };
  }, [open, goldsmithId]);

  // Load requirements when order changes
  useEffect(() => {
    Promise.resolve().then(() => {
      setRequirementId('');
      setRequirements([]);
    });
    if (!orderId) return;
    let cancelled = false;
    api
      .get(`/orders/${orderId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const reqs = (data.requirements || []).filter((r) => r.status !== 'complete');
        const matchingMat = meta?.material_type || 'diamond';
        // Phase 16 — Match requirement to source material exactly. Untyped
        // legacy requirements are treated as diamond-compatible.
        const filtered = reqs.filter((r) =>
          (r.material_type === matchingMat) || (!r.material_type && matchingMat === 'diamond')
        );
        setRequirements(filtered);
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [orderId, isGold, meta]);

  const avail = meta?.available || {};
  const overCt = !isGold && Number(carats || 0) - (avail.carats || 0) > 1e-9;
  const overPcs = !isGold && Number(pieces || 0) > (avail.pieces || 0);
  const overG = isGold && Number(grams || 0) - (avail.grams || 0) > 1e-6;
  const over = overCt || overPcs || overG;

  const canSubmit = useMemo(() => {
    if (!orderId || over || submitting) return false;
    if (isGold) return Number(grams || 0) > 0;
    return Number(carats || 0) > 0 || Number(pieces || 0) > 0;
  }, [orderId, over, submitting, isGold, grams, carats, pieces]);

  const submit = async () => {
    setSubmitting(true);
    try {
      if (poolMode) {
        // Pooled attribution — backend allocates FIFO across every
        // unattributed source line of this classification.
        const payload = {
          goldsmith_id: goldsmithId,
          material_type: pool.material_type,
          purity_id: pool.purity_id || null,
          packet_id: pool.packet_id || null,
          order_id: orderId,
          requirement_id: requirementId || null,
          mark_requirement_complete: !!markComplete,
          note: note || '',
        };
        if (isGold) payload.grams = Number(grams || 0);
        else {
          payload.carats = Number(carats || 0);
          payload.pieces = Number(pieces || 0);
        }
        await api.post('/goldsmith-issues/attribute-pool', payload);
      } else {
        const payload = {
          line_id: lineId,
          issue_id: issueId,
          order_id: orderId,
          requirement_id: requirementId || null,
          mark_requirement_complete: !!markComplete,
          note: note || '',
        };
        if (isGold) payload.grams = Number(grams || 0);
        else {
          payload.carats = Number(carats || 0);
          payload.pieces = Number(pieces || 0);
        }
        await api.post('/goldsmith-issues/attribute', payload);
      }
      toast.success('Attributed successfully');
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(extractError(e, 'Attribution failed'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{poolMode ? 'Attribute from Unattributed Pool' : 'Attribute to an Order'}</DialogTitle>
          <DialogDescription>
            {poolMode
              ? 'Move part (or all) of this classification\u2019s pooled unattributed material onto a specific order. The system draws FIFO across the underlying issue memos.'
              : 'Move part (or all) of this unattributed material onto a specific order. Quantities cannot exceed what the goldsmith currently holds unattributed.'}
          </DialogDescription>
        </DialogHeader>

        {/* Source context */}
        <Card className="p-3 bg-[var(--paper-warm)] text-xs space-y-1" data-testid="attr-source-card">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="capitalize">{meta?.material_type}</Badge>
            <span className="font-mono text-[var(--ink-navy)]">
              {isGold ? (meta?.purity_name) : `${meta?.packet_no}${meta?.classification_schema ? ` \u00b7 ${meta.classification_schema}` : ''}`}
            </span>
            {poolMode
              ? <Badge className="bg-slate-100 text-slate-700 text-[10px]">Pooled</Badge>
              : (issueMemoNo && <Badge className="bg-slate-100 text-slate-700 text-[10px] font-mono">{issueMemoNo}</Badge>)}
          </div>
          <div className="text-slate-500">
            Available (Unattributed{poolMode ? ' Pool' : ''}):{' '}
            {isGold
              ? <span className="font-mono">{formatGrams(avail.grams || 0)} g</span>
              : <span className="font-mono">{formatCarats(avail.carats || 0)} ct \u00b7 {avail.pieces || 0} pcs</span>}
          </div>
        </Card>

        <div className="space-y-3">
          <div>
            <Label>Target Order <span className="text-rose-600">*</span></Label>
            <SearchableSelect
              items={orders}
              value={orderId}
              onValueChange={(v) => setOrderId(v || '')}
              itemToLabel={(o) => `${o.order_no} — ${o.title}`}
              searchKeys={['order_no', 'title']}
              placeholder={orders.length === 0 ? 'No active orders' : 'Select an active order'}
              searchPlaceholder="Search order no / title…"
              disabled={orders.length === 0}
              testId="attr-order-select"
            />
          </div>

          {orderId && (
            <div>
              <Label>Requirement (optional)</Label>
              {(() => {
                const reqItems = requirements.map((r) => ({
                  id: r.id,
                  label: r.classification_label || r.description || 'Requirement',
                }));
                const items = [{ id: 'none', label: 'None' }, ...reqItems];
                return (
                  <SearchableSelect
                    items={items}
                    value={requirementId || 'none'}
                    onValueChange={(v) => setRequirementId(!v || v === 'none' ? '' : v)}
                    itemToValue={(it) => it.id}
                    itemToLabel={(it) => it.label}
                    searchKeys={['label']}
                    placeholder="None"
                    searchPlaceholder="Search requirement…"
                    testId="attr-req-select"
                  />
                );
              })()}
            </div>
          )}

          {isGold ? (
            <div>
              <Label>Grams <span className="text-rose-600">*</span></Label>
              <Input
                type="number" step="0.001" value={grams}
                onChange={(e) => setGrams(e.target.value)}
                data-testid="attr-grams-input"
                placeholder={`Max ${avail.grams || 0}`}
              />
              {overG && <div className="text-[11px] text-rose-600 mt-1">Cannot exceed available {avail.grams || 0} g</div>}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Carats</Label>
                <Input
                  type="number" step="0.01" value={carats}
                  onChange={(e) => setCarats(e.target.value)}
                  data-testid="attr-carats-input"
                  placeholder={`Max ${avail.carats || 0}`}
                />
                {overCt && <div className="text-[11px] text-rose-600 mt-1">Max {avail.carats || 0} ct</div>}
              </div>
              <div>
                <Label>Pieces</Label>
                <Input
                  type="number" step="1" value={pieces}
                  onChange={(e) => setPieces(e.target.value)}
                  data-testid="attr-pieces-input"
                  placeholder={`Max ${avail.pieces || 0}`}
                />
                {overPcs && <div className="text-[11px] text-rose-600 mt-1">Max {avail.pieces || 0} pcs</div>}
              </div>
            </div>
          )}

          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="attr-note-input" />
          </div>

          {requirementId && (
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Checkbox
                checked={markComplete}
                onCheckedChange={(v) => setMarkComplete(!!v)}
                data-testid="attr-mark-complete"
              />
              <span>Mark linked requirement as fully met after this attribution</span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="luxury-button"
            data-testid="attr-submit-button"
          >
            {submitting ? 'Attributing…' : 'Attribute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
